import { DAYS, TIME_SLOTS, JUNIOR_ELIGIBLE_SLOTS } from "./tiers";
import { API_URL } from "./api";
import { apiFetch } from "./auth";

// A membership application is a person's own submission from the public
// sign-up form (/?join). It is deliberately NOT a player: it lives in its own
// file server-side and only becomes a roster entry once a coach approves it.
// That keeps an unauthenticated endpoint from ever writing to kac_data.json.

export function blankApplication() {
  return {
    firstName: "",
    lastName: "",
    dob: "",
    classification: "",
    gender: "",
    email: "",
    phone: "",
    address: "",
    contactMethod: "",
    tier: "",
    skillLevel: "",
    preferredPartners: "",
    guardianName: "",
    guardianRelationship: "",
    guardianPhone: "",
    guardianEmail: "",
    emergencyName: "",
    emergencyPhone: "",
    availability: [],
    privateInterest: false,
    notes: "",
  };
}

// Age in whole years on `today` (defaults to now). Returns null for a missing
// or unparseable date rather than guessing.
export function ageFromDob(dob, today = new Date()) {
  if (!dob) return null;
  const born = new Date(dob);
  if (isNaN(born.getTime())) return null;
  let age = today.getFullYear() - born.getFullYear();
  const m = today.getMonth() - born.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < born.getDate())) age--;
  return age < 0 || age > 120 ? null : age;
}

// Under-18s go on the junior roster. Used to pre-select the classification
// once a date of birth is entered, and to decide whether guardian details are
// required — the member can still override the classification itself.
export function suggestedClassification(dob) {
  const age = ageFromDob(dob);
  if (age === null) return "";
  return age < 18 ? "Junior" : "Adult";
}

// Juniors are only ever scheduled in the after-school block, so showing them
// all 16 hourly slots (6 AM–10 PM) is noise on a phone. Adults can train any
// time the club is open.
export function slotsFor(classification) {
  return classification === "Junior" ? JUNIOR_ELIGIBLE_SLOTS : TIME_SLOTS;
}

export const AVAILABILITY_DAYS = DAYS;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// Accepts +country codes, spaces, dashes, parens and dots; needs 7+ digits.
const PHONE_RE = /^\+?[\d\s().-]{7,}$/;

function isEmail(v) {
  return EMAIL_RE.test(String(v).trim());
}

function isPhone(v) {
  return PHONE_RE.test(String(v).trim()) && (String(v).match(/\d/g) || []).length >= 7;
}

// Field-keyed errors so each input can show its own message. Everything here
// is required except address, contact method, skill level, preferred
// partners, private-lesson interest and notes.
export function validateApplication(a) {
  const errors = {};
  const req = (field, label) => {
    if (!String(a[field] ?? "").trim()) errors[field] = `${label} is required.`;
  };

  req("firstName", "First name");
  req("lastName", "Last name");
  req("dob", "Date of birth");
  req("classification", "Adult or junior");
  req("gender", "Gender");
  req("tier", "Group level");

  if (a.dob && ageFromDob(a.dob) === null) {
    errors.dob = "Please enter a valid date of birth.";
  }

  if (!String(a.email).trim()) errors.email = "Email is required.";
  else if (!isEmail(a.email)) errors.email = "Please enter a valid email address.";

  if (!String(a.phone).trim()) errors.phone = "Mobile number is required.";
  else if (!isPhone(a.phone)) errors.phone = "Please enter a valid mobile number.";

  // Guardian and emergency details are required for juniors only.
  if (a.classification === "Junior") {
    req("guardianName", "Parent/guardian name");
    req("guardianRelationship", "Relationship to the member");
    req("emergencyName", "Emergency contact name");

    if (!String(a.guardianPhone).trim()) errors.guardianPhone = "Parent/guardian mobile is required.";
    else if (!isPhone(a.guardianPhone)) errors.guardianPhone = "Please enter a valid mobile number.";

    if (!String(a.guardianEmail).trim()) errors.guardianEmail = "Parent/guardian email is required.";
    else if (!isEmail(a.guardianEmail)) errors.guardianEmail = "Please enter a valid email address.";

    if (!String(a.emergencyPhone).trim()) errors.emergencyPhone = "Emergency contact number is required.";
    else if (!isPhone(a.emergencyPhone)) errors.emergencyPhone = "Please enter a valid phone number.";
  }

  if (!a.availability || a.availability.length === 0) {
    errors.availability = "Please pick at least one time you could attend.";
  }

  return errors;
}

// Turn an approved application into a roster player matching the schema the
// manual Add-player path and the file importer both produce. `id` is assigned
// by the caller, which knows the existing roster.
export function applicationToPlayer(app, id) {
  const first = String(app.firstName || "").trim();
  const last = String(app.lastName || "").trim();
  return {
    id,
    name: `${first} ${last}`.trim(),
    lastName: last,
    classification: app.classification || "Adult",
    tier: app.tier || "No Group",
    age: ageFromDob(app.dob),
    dob: app.dob || "",
    gender: app.gender || "",
    email: String(app.email || "").trim(),
    phone: String(app.phone || "").trim(),
    address: app.address || "",
    contactMethod: app.contactMethod || "",
    skillLevel: app.skillLevel || "",
    groupId: null,
    availability: app.availability || [],
    preferredPartners: app.preferredPartners || "",
    privateTimes: "",
    notes: app.notes || "",
    ...(app.classification === "Junior"
      ? {
          guardianName: app.guardianName || "",
          guardianRelationship: app.guardianRelationship || "",
          guardianPhone: app.guardianPhone || "",
          guardianEmail: app.guardianEmail || "",
          emergencyName: app.emergencyName || "",
          emergencyPhone: app.emergencyPhone || "",
        }
      : {}),
    // Provenance, so an approved sign-up is distinguishable from a
    // staff-entered or imported player later on.
    source: "signup",
    applicationRef: app.ref || "",
  };
}

// The next free pXXX id given the current roster.
export function nextPlayerId(players) {
  const max = (players || []).reduce((m, p) => {
    const n = parseInt(String(p.id).replace(/\D/g, ""), 10);
    return isNaN(n) ? m : Math.max(m, n);
  }, 0);
  return `p${String(max + 1).padStart(3, "0")}`;
}

// --- Server calls --------------------------------------------------------


// Public: append one application. No secret — the endpoint only ever appends
// to the applications file and returns the reference number it assigned.
export async function submitApplication(app) {
  const res = await fetch(`${API_URL}?apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(app),
  });
  if (!res.ok) throw new Error("Submission failed");
  const data = await res.json();
  if (!data || !data.ok) throw new Error(data?.error || "Submission failed");
  return data.ref;
}

// Staff: read every application (pending and resolved).
export async function fetchApplications() {
  const res = await apiFetch("?applications");
  if (!res.ok) throw new Error("Could not load applications");
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

// Staff: mark one application approved or declined. The server keeps the row
// so there's a record of who applied and what happened to them.
export async function resolveApplication(id, status) {
  const res = await apiFetch("?applications", {
    method: "POST",
    body: JSON.stringify({ id, status }),
  });
  if (!res.ok) throw new Error("Could not update the application");
  return res.json();
}
